import React, { useState, useEffect } from 'react'
import LiveChatWidget from '../support/LiveChatWidget'
import { getCurrentUser } from '../../lib/supabase'
import { getSessionHeaders } from '../../lib/serverSession'

interface LiveChatProviderProps {
  children: React.ReactNode
}

// ---------------------------------------------------------------------------
// HARD KILL-SWITCH — v4.7.14
// The AI chatbot is fully disabled (Gemini quota exhausted; feature parked
// until a paid-tier key or alternative provider is wired in). Flip this to
// `false` to re-enable. Mirror the matching switch in api/user/chatbot.js.
// ---------------------------------------------------------------------------
const CHATBOT_HARD_DISABLED = true

/**
 * LiveChatProvider — v7.3.345
 *
 * Wraps the app to provide the AI chatbot widget. Visibility is gated by:
 *  1. User must be authenticated.
 *  2. The `chatbot_enabled` system setting must be 'true' (public mode), OR
 *     the current user must be an admin (admin testing mode).
 *
 * This allows admins to fully test the Gemini integration, voice input,
 * and escalation workflows without exposing the feature to live investors
 * until they flip the toggle in System Settings.
 */
const LiveChatProvider: React.FC<LiveChatProviderProps> = ({ children }) => {
  const [chatOpen, setChatOpen] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [chatbotVisible, setChatbotVisible] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (CHATBOT_HARD_DISABLED) { setLoading(false); return }
    checkVisibility()
  }, [])

  const checkVisibility = async () => {
    try {
      // 1. Check authentication
      const user = await getCurrentUser()
      if (!user) {
        setIsAuthenticated(false)
        setChatbotVisible(false)
        return
      }
      setIsAuthenticated(true)

      // 2. Check chatbot feature flag (includes admin override server-side)
      try {
        const response = await fetch('/api/user/system-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ operation: 'check_chatbot_visibility' })
        })

        if (response.ok) {
          const data = await response.json()
          setChatbotVisible(data.visible === true)
        } else {
          // If the API fails, default to hidden for safety
          setChatbotVisible(false)
        }
      } catch {
        // Network error — default to hidden
        setChatbotVisible(false)
      }
    } catch (error) {
      console.error('Error checking chat visibility:', error)
      setIsAuthenticated(false)
      setChatbotVisible(false)
    } finally {
      setLoading(false)
    }
  }

  // Don't show chat widget if hard-disabled, not authenticated, not visible, or still loading
  if (CHATBOT_HARD_DISABLED || loading || !isAuthenticated || !chatbotVisible) {
    return <>{children}</>
  }

  return (
    <>
      {children}
      <LiveChatWidget
        isOpen={chatOpen}
        onToggle={() => setChatOpen(!chatOpen)}
        position="bottom-right"
      />
    </>
  )
}

export default LiveChatProvider
